import { h as __h } from "preact";
import { signal, untracked } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, createRef } from "preact";
/** @import { ComponentChildren, RefObject } from "preact"; */

import html from "~/util/html.js";
import Modal from "~/component/Modal.js";

/**
 * @typedef {object} ModalEntry
 * @property {string} id
 * @property {string | ComponentChildren | undefined} title
 * @property {boolean | undefined} closable
 * @property {(resolve: (result?: any) => void) => ComponentChildren} content
 * @property {(result?: any) => void} resolve
 */

/** @type {Signal<ModalEntry | null>} */
const current = signal(null);

/**
 * @template [T=void]
 * @param {object} options
 * @param {string | ComponentChildren} [options.title]
 * @param {boolean} [options.closable]
 * @param {(resolve: (result?: T) => void) => ComponentChildren} options.content
 * @returns {Promise<T | undefined>} Settles with the resolved result, or undefined on dismissal
 */
export function modal({
  title,
  closable,
  content
}) {
  const id = crypto.randomUUID();

  /** @type {PromiseWithResolvers<T | undefined>} */
  const {
    promise,
    resolve: settle
  } = Promise.withResolvers();

  /** @param {T} [result] */
  const resolve = result => {
    if (untracked(() => current.value)?.id === id) current.value = null;
    settle(result);
  };
  untracked(() => current.value)?.resolve();
  current.value = {
    id,
    title,
    closable,
    content,
    resolve
  };
  return promise;
}
export function ModalProvider() {
  const entry = current.value;
  if (!entry) return null;
  return __h(SpawnedModal, {
    key: entry.id,
    entry: entry
  });
}

/**
 * @extends {Component<SpawnedProps>}
 *
 * @typedef {object} SpawnedProps
 * @property {ModalEntry} entry
 */
class SpawnedModal extends Component {
  /** @type {RefObject<Modal>} */
  #modal = createRef();

  /** @override */
  componentWillUnmount() {
    this.#modal.current?.close();
  }
  #onClose = () => {
    this.props.entry.resolve(undefined);
  };
  render() {
    const {
      entry
    } = this.props;
    return __h(Modal, {
      ref: this.#modal,
      id: entry.id,
      title: entry.title,
      closable: entry.closable,
      open: true,
      onClose: this.#onClose
    }, entry.content(entry.resolve));
  }
}